import { CSSProperties, ReactNode } from 'react';
import Icon from './Icon';
import { ThemeColors, useTheme } from '../context/ThemeContext';

export const MIN_TOUCH_TARGET = 48;

interface IconButtonProps {
  icon: string;
  accessibilityLabel: string;
  onPress: () => void;
  color?: string;
  backgroundColor?: string;
  solid?: boolean;
  style?: CSSProperties;
}

export function IconButton({
  icon, accessibilityLabel, onPress, color, backgroundColor, style,
}: IconButtonProps) {
  const { colors } = useTheme();
  return (
    <button
      aria-label={accessibilityLabel}
      onClick={onPress}
      style={{
        width: MIN_TOUCH_TARGET,
        height: MIN_TOUCH_TARGET,
        borderRadius: 24,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: backgroundColor || colors.surfaceMuted,
        border: 'none',
        cursor: 'pointer',
        ...style,
      }}
    >
      <Icon name={icon} size={16} color={color || colors.text} />
    </button>
  );
}

interface ScreenHeaderProps {
  title: string;
  icon?: string;
  right?: ReactNode;
  colors?: ThemeColors;
}

export function ScreenHeader({ title, icon, right, colors: propColors }: ScreenHeaderProps) {
  const theme = useTheme();
  const c = propColors || theme.colors;
  return (
    <div
      style={{
        minHeight: 72,
        display: 'flex',
        alignItems: 'center',
        padding: '12px 20px',
        borderBottom: `1px solid ${c.headerBorder}`,
        backgroundColor: c.header,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        paddingTop: 'calc(12px + env(safe-area-inset-top, 0px))',
      }}
    >
      {icon ? <Icon name={icon} size={20} color={c.accentDark} style={{ marginRight: 10 }} /> : null}
      <div style={{ flex: 1, fontSize: 28, fontWeight: 700, color: c.text }}>{title}</div>
      {right ? <div style={{ marginLeft: 12 }}>{right}</div> : null}
    </div>
  );
}

export function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  const { colors } = useTheme();
  return (
    <div
      style={{
        backgroundColor: colors.card,
        border: `1px solid ${colors.cardBorder}`,
        borderRadius: 14,
        padding: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

interface EmptyStateProps {
  icon: string;
  title: string;
  body?: string;
}

export function EmptyState({ icon, title, body }: EmptyStateProps) {
  const { colors } = useTheme();
  return (
    <div
      style={{
        padding: '44px 28px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        textAlign: 'center',
      }}
    >
      <Icon name={icon} size={34} color={colors.textMuted} />
      <div style={{ fontSize: 20, fontWeight: 700, color: colors.text, marginTop: 6 }}>{title}</div>
      {body ? <div style={{ fontSize: 16, lineHeight: 1.55, color: colors.textSub }}>{body}</div> : null}
    </div>
  );
}

interface InfoBannerProps {
  icon?: string;
  children: ReactNode;
  tone?: 'info' | 'warning' | 'success' | 'danger';
}

export function InfoBanner({ icon = 'info-circle', children, tone = 'info' }: InfoBannerProps) {
  const { colors } = useTheme();
  const palette = {
    info: { bg: colors.infoSoft, text: colors.infoText, border: colors.infoText },
    warning: { bg: colors.warningSoft, text: colors.warningText, border: colors.warningText },
    success: { bg: colors.successSoft, text: colors.successText, border: colors.successText },
    danger: { bg: colors.dangerSoft, text: colors.dangerText, border: colors.dangerText },
  }[tone];
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        backgroundColor: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 12,
        padding: 12,
      }}
    >
      <Icon name={icon} size={14} color={palette.text} style={{ marginTop: 2 }} />
      <div style={{ flex: 1, fontSize: 14, lineHeight: 1.5, fontWeight: 600, color: palette.text }}>
        {children}
      </div>
    </div>
  );
}
